import { IDisplayPairing, IScrollClickResponse, isIPairingPosition, ScrollBarClick } from "@vmosolutions/oc-interfaces";
import moment from "moment-timezone";
import { IRowItem } from "src/pages/PairingWaterfall/PairingWaterfallList";
import { IBaseTime, ICrewEvent } from "src/redux/reducers/CrewScheduleReducer";
import { BaseTimelineGL } from "../../../SharedGL/BaseTimelineGL";
import { Camera } from "../../../SharedGL/Camera";
import { IOutlineBox, OutlinesGL } from "../../../SharedGL/OutLines/OutlinesGL";
import { ScrollBarWrapper } from "../../../SharedGL/ScrollBar/ScrollBarWrapper";
import { IRGBA } from "../../FlightTimeline/TimelineUtils";
import { ICrewMemberGLDimensions } from "../CrewScheduleCanvas";
import { CrewScheduleErrorsGL } from "./CrewScheduleErrorsGL";
import { PairingsGL } from "./PairingsGL";
import { PairingShader } from "./shaders/PairingShader";

export interface IPairingMapItem {
  lastModified: number;
  pairings: IDisplayPairing[];
}
export interface IPairingMap {
  [pairingId: string]: IPairingMapItem;
}
export interface IShortIdGL {
  rowStart: number;
  rowHeight: number;
  isSelected: boolean;
  isExpanded: boolean;
  pairingsGL?: PairingsGL;
}

export interface IRowItemPairings {
  lastModified: number;
  pairings: IDisplayPairing[];
}

export interface IShortIdGLMap {
  [shortId: string]: IShortIdGL;
}

export const oneDayMillis = 8.64e7;

export const baseRowHeight = 36; // Default height for waterfall rows.
//export const expandedRowHeight = 80;
export class WaterfallGL extends BaseTimelineGL {
  private rowList: IRowItem[];
  private waterfallGLMap: IShortIdGLMap;
  private pairingShader: PairingShader;
  private hoveredEvents: ICrewEvent[];
  private focusedEvents: ICrewEvent[];
  private outlinedEvents: ICrewEvent[]; // renamed from selectedEvents because some selected events might not be visible
  private outlinesGL: OutlinesGL;
  private selectedOutlinesGL: OutlinesGL;
  private focusedOutlinesGL: OutlinesGL;
  private lastModifiedTime: number;
  private dayScale: number;
  private numDaysInView: number;
  private scrollBarWrapper: ScrollBarWrapper;
  private scrollBarThumbColor: IRGBA;
  private rowPairings: IPairingMap;
  private focusedEventOutlineColor: IRGBA;

  constructor(mount: HTMLElement, width: number, height: number) {
    super(mount, width, height);
    this.rowList = [];
    this.waterfallGLMap = {};
    this.scrollBarWrapper = new ScrollBarWrapper(this.gl);
    this.scrollBarThumbColor = this.theme.scrollBarThumb;
    this.hoveredEvents = [];
    this.outlinedEvents = [];
    this.outlinesGL = new OutlinesGL(this.gl);
    this.selectedOutlinesGL = new OutlinesGL(this.gl);
    this.focusedOutlinesGL = new OutlinesGL(this.gl);
    this.updateDayScale();
    this.pairingShader = new PairingShader(this.gl);
    this.rowPairings = {};
    this.focusedEventOutlineColor = this.theme.selectedFlightColor;
  }

  public setBaseTime(baseTime: IBaseTime): void {
    super.setBaseTime(baseTime);
  }

  /**
   * getRowFromYPixel -
   * @param yPixel - the yPixel to find the crew member
   */
  private getRowFromYPixel(yPixel: number): any {
    const [yMin] = this.camera.getYRange();
    const y = yPixel + yMin - BaseTimelineGL.timelineStart;
    for (let i = this.minRowIndex; i <= this.maxRowIndex; i++) {
      const _id = this.rowList[i]?._id;
      if (!_id) {
        console.warn("You are trying to access the index of an row list that doesn't exist");
        continue;
      }
      const itemGL = this.waterfallGLMap[_id];
      if (y >= itemGL.rowStart && y <= itemGL.rowStart + itemGL.rowHeight) {
        return itemGL;
      }
    }
    return null;
  }

  /**
   * getYPixelFromCrewMember -
   * @param crewId - the crewId needed to return Ypos.
   */
  public getYPixelFromCrewMember(crewId: string): number {
    //const [yMin] = this.camera.getYRange();
    const crewGL = this.waterfallGLMap[crewId];
    if (crewGL) {
      return crewGL.rowStart + crewGL.rowHeight / 2;
    }
    return null;
  }

  public getCrewMemberDimensions(crewId: string): ICrewMemberGLDimensions | null {
    const crewGL = this.waterfallGLMap[crewId];
    if (!crewGL) {
      return null;
    }
    const { rowStart, rowHeight, isExpanded } = crewGL;
    return {
      rowStart,
      rowHeight,
      isExpanded,
    };
  }

  public getHoveredCrewMember(x: number, yPixel: number): any {
    return this.getRowFromYPixel(yPixel);
  }

  /**
   * set the selected events outline color, true = default, false = same as hover
   */
  public setFocusedEventsOutlineColor = (defaultColor: boolean): void => {
    this.focusedEventOutlineColor = defaultColor ? this.theme.selectedFlightColor : this.theme.hoverFlightColor;
  };

  /**
   * getSelectedEventsOutlineColor returns true if the deafult color is the current color
   */
  public getFocusedEventsOutlineColor = (): boolean => {
    return this.focusedEventOutlineColor === this.theme.selectedFlightColor;
  };

  public setHoveredTime = (time: number): number => {
    return (this.hoveredTime = time);
  };

  public recalculateScrollBar = (): void => {
    this.scrollBarWrapper.calculateScrollBar(this.camera, this.canvas, this.maxHeight); //sets the pixels for size of bar
  };

  public updateItem(items: IRowItem[], disableExpandedRow?: boolean): void {
    this.rowList = items;
    let rowStart = 0;
    // let index = 0;
    let rowHeight = baseRowHeight;
    let rowSizeChanged = false;
    for (const item of items) {
      // New parameter disableExpandedRow disables expanded row appearance.
      rowHeight = baseRowHeight;
      if (!this.waterfallGLMap[item._id]) {
        const pairingsGL = new PairingsGL(this.gl, this.pairingShader);
        this.waterfallGLMap[item._id] = {
          ...item,
          rowStart,
          rowHeight,
          pairingsGL,
        };
      }
      this.waterfallGLMap[item._id].isSelected = item.isSelected;
      rowSizeChanged = rowSizeChanged || item.isExpanded !== this.waterfallGLMap[item._id].isExpanded;
      this.waterfallGLMap[item._id].isExpanded = item.isExpanded;
      this.waterfallGLMap[item._id].rowStart = rowStart;
      this.waterfallGLMap[item._id].rowHeight = rowHeight;
      // index++;
      rowStart += rowHeight;
    }
    this.maxHeight = rowStart;
    if (rowSizeChanged) {
      this.setOutlinedEvents(this.outlinedEvents);
    }
    this.updateMinMaxRows();
    this.recalculateScrollBar();
    this.updateCameraRange();
  }

  //snap to spot clicked
  public scrollClick(xPos: number, yPos: number): ScrollBarClick {
    const response: IScrollClickResponse = this.scrollBarWrapper.clickedOnScrollBar(
      this.gl,
      this.camera,
      this.canvas,
      xPos,
      yPos,
      this.maxHeight
    );
    if (response.typeOfScroll === 5) {
      this.scrollBarThumbColor = this.theme.scrollBarThumbClickedOn;
    } else {
      this.scrollBarThumbColor = this.theme.scrollBarThumb;
    }
    this.moveCameraByYPixels(response.pixelsToMove);
    return response.typeOfScroll;
  }

  public hoveredOnScrollBarThumb(xPos: number, yPos: number): void {
    const response: boolean = this.scrollBarWrapper.hoveredOnScrollBarThumb(
      this.camera,
      this.canvas,
      xPos,
      yPos,
      this.maxHeight
    );
    if (response) {
      this.scrollBarThumbColor = this.theme.scrollBarThumbHover;
    } else {
      this.scrollBarThumbColor = this.theme.scrollBarThumb;
    }
  }

  //drag scroll bar
  public dragScrollBarThumb(yMovement: number): void {
    const [yStart, yEnd] = this.camera.getYRange(); //camera coordinates
    const pixelHeight: number = yEnd - yStart;
    const pixelsToMove: number = yMovement * (this.maxHeight / pixelHeight);
    this.moveCameraByYPixels(pixelsToMove);
  }

  public updatePairings(rowPairings: IPairingMap): void {
    let didUpdate = false;
    this.rowPairings = rowPairings;
    for (const item of this.rowList) {
      const pairingGL = this.waterfallGLMap[item._id];
      const rowItemPairings: IRowItemPairings = rowPairings[item._id];
      if (!pairingGL || !rowItemPairings) {
        continue;
      }
      if (pairingGL.pairingsGL.lastModified >= rowItemPairings.lastModified) {
        continue;
      }
      didUpdate = true;
      pairingGL.pairingsGL.updatePairingBuffers(this.gl, rowItemPairings.pairings);
    }
    if (didUpdate) {
      this.lastModifiedTime = Date.now();
    }
  }

  public getHoveredEvents(): ICrewEvent[] {
    return this.hoveredEvents;
  }

  public setHoveredEvents(hoveredEvents: ICrewEvent[], yPixel: number): void {
    this.hoveredEvents = [...hoveredEvents];
    try {
      const eventOutlines = this.setOutlineEvents(hoveredEvents);
      this.outlinesGL.updateBuffers(this.gl, eventOutlines);
    } catch (e) {
      console.log(e);
    }
  }

  private setOutlineEvents(outlinedEvents: ICrewEvent[]): IOutlineBox[] {
    const mapToOutlines = (selectedEvent: ICrewEvent): IOutlineBox | null => {
      const { event, crewId } = selectedEvent;
      const crewGL = this.waterfallGLMap[crewId];
      if (!crewGL || (!crewGL.isExpanded && selectedEvent.isPlanned)) {
        return null;
      }
      const yStart = PairingsGL.pairingYStart;
      const baseHeight = PairingsGL.pairingHeight;
      const y0 = yStart;
      const height = baseHeight;
      return {
        x0: event.start?.valueOf() - Camera.referenceTime,
        x1: event.end?.valueOf() - Camera.referenceTime,
        y0: y0 + crewGL.rowStart,
        y1: y0 + crewGL.rowStart + height,
      };
    };
    const eventOutlines = outlinedEvents.map(mapToOutlines);

    return eventOutlines.filter((val: IOutlineBox) => val);
  }

  public redrawOutlines(): void {
    const eventOutlines = this.setOutlineEvents(this.outlinedEvents);
    this.selectedOutlinesGL.updateBuffers(this.gl, eventOutlines);
  }

  public setOutlinedEvents(outlinedEvents: ICrewEvent[]): void {
    this.outlinedEvents = [...outlinedEvents];
    const eventOutlines = this.setOutlineEvents(outlinedEvents);
    this.selectedOutlinesGL.updateBuffers(this.gl, eventOutlines);
  }

  public setFocusedEvents(focusedEvents: ICrewEvent[]): void {
    this.focusedEvents = [...focusedEvents];
    const eventOutlines = this.setOutlineEvents(focusedEvents);
    this.focusedOutlinesGL.updateBuffers(this.gl, eventOutlines);
  }

  private getOutlineDays(shortIdGL: IShortIdGL, outlineTimes: [moment.Moment, moment.Moment]): IOutlineBox {
    const yStart = CrewScheduleErrorsGL.yHeight;
    const height = baseRowHeight;
    const [start, end] = outlineTimes;
    const startTime = this.baseTime.isUTC ? moment(start).utc() : moment(start).tz(this.baseTime.timezoneId, true);
    const endTime = this.baseTime.isUTC ? moment(end).utc() : moment(end).tz(this.baseTime.timezoneId, true);
    return {
      x0: startTime?.valueOf() - Camera.referenceTime,
      x1: endTime?.valueOf() - Camera.referenceTime,
      y0: shortIdGL.rowStart + yStart,
      y1: shortIdGL.rowStart + yStart + height - CrewScheduleErrorsGL.yHeight,
    };
  }

  // Override
  public zoomCamera = (deltaZoom: number): void => {
    super.zoomCamera(deltaZoom);
    const zoomTime = this.pixelCoordsToTimeAxis(deltaZoom);

    this.camera.zoom(zoomTime);
    this.updateDayScale();
  };

  private updateDayScale(): void {
    const [minTime, maxTime] = this.camera.getXRange();

    this.dayScale = oneDayMillis;
    this.numDaysInView = (maxTime - minTime) / oneDayMillis + 1;
  }

  // public updateItemPairing(pairing: IDisplayPairing): void {}

  public updateMinMaxRows(): void {
    const [minY, maxY] = this.camera.getYRange();
    let foundMin = false;
    let foundMax = false;
    let index = 0;

    for (const crewMember of this.rowList) {
      const crewId = isIPairingPosition(crewMember) ? `${crewMember.position}-${index}` : crewMember._id;

      const crewGL = this.waterfallGLMap[crewId];
      if (!crewGL) {
        return;
      }
      if (!foundMin && crewGL.rowStart + crewGL.rowHeight > minY) {
        this.minRowIndex = index;
        foundMin = true;
      }

      if (!foundMax && crewGL.rowStart + crewGL.rowHeight > maxY) {
        this.maxRowIndex = index;
        foundMax = true;
      }

      if (foundMin && foundMax) {
        return;
      }
      index++;
    }

    this.minRowIndex = foundMin ? this.minRowIndex : 0;
    this.maxRowIndex = foundMax ? this.maxRowIndex : this.rowList.length - 1;
  }

  public setViewport(width: number, height: number): void {
    super.setViewport(width, height);

    this.updateMinMaxRows();
    this.updateDayScale();
    this.recalculateScrollBar();
  }

  public moveCameraByYPixels(pixels: number): void {
    super.moveCameraByYPixels(pixels);
    this.updateMinMaxRows();
  }

  public draw(): void {
    super.draw();

    if (this.rowList.length === 0) {
      return;
    }

    this.colPainter.draw(this.gl, this.camera, this.dayScale, this.numDaysInView, this.theme.oddRow, this.baseTime);

    const white: IRGBA = {
      red: 1.0,
      green: 1.0,
      blue: 1.0,
      alpha: 0.2,
    };

    for (let i = this.minRowIndex; i <= this.maxRowIndex; i++) {
      const item = this.rowList[i];
      const rowItemGL = this.waterfallGLMap[item._id];
      rowItemGL.pairingsGL.draw(this.gl, this.camera, rowItemGL.rowStart);
      if (!!this.rowPairings && !!this.rowPairings[item._id]) {
        this.drawPairingLabels(this.camera, this.rowPairings[item._id].pairings, rowItemGL.rowStart);
        // const checkInOverrides = rowItemGL.pairingsGL?.getcheckInOuts();
        // drawCheckInOutIndicators(this.timelineLabelsGL.getContext(), this.camera, rowItemGL, checkInOverrides);
      }
    }

    this.outlinesGL.draw(this.gl, this.camera, this.theme.hoverFlightColor, -1.4);
    this.selectedOutlinesGL.draw(this.gl, this.camera, this.theme.selectedFlightColor, -1.1);
    this.focusedOutlinesGL.draw(this.gl, this.camera, this.focusedEventOutlineColor, -1.0);
    this.scrollBarWrapper.drawBar(
      this.gl,
      this.camera,
      this.canvas,
      this.theme.scrollBarBackGround,
      this.scrollBarThumbColor ?? this.theme.scrollBarThumb,
      this.maxHeight
    );
  }
}
